import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { trackTikTokEvent } from "@/lib/tiktokEvents";
import { Check, X, Sparkles, Building2, ArrowRight, Zap, Gift } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { usePlans } from "@/hooks/usePlans";
import type { Plan } from "@/lib/plans";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useTranslation } from "@/i18n";
import { Skeleton } from "@/components/ui/skeleton";

interface FeatureDisplay {
  label: string;
  included: boolean;
  highlight?: boolean;
}

const Pricing = () => {
  const { t } = useTranslation();
  const { plans, isLoading } = usePlans();
  const [isAnnual, setIsAnnual] = useState(true);

  // Generate translated features for a plan
  const getPlanFeaturesTranslated = (plan: Plan): FeatureDisplay[] => {
    const features: FeatureDisplay[] = [];
    
    // Credits limit (unified)
    features.push({
      label: t('pricing.featureLabels.creditsLimit').replace('{count}', String(plan.creditsMonthly)),
      included: true,
    });
    
    // Store limit (for multi-store plans)
    if (plan.storeLimit > 1) {
      features.push({
        label: t('pricing.featureLabels.storesLimit').replace('{count}', String(plan.storeLimit)),
        included: true,
        highlight: true,
      });
    }
    
    // Categories
    if (plan.categoryLimitMonthly === null) {
      features.push({
        label: t('pricing.featureLabels.categoriesUnlimited'),
        included: true,
      });
    } else if (plan.categoryLimitMonthly === 0) {
      features.push({
        label: t('pricing.featureLabels.categoriesNone'),
        included: false,
      });
    } else {
      features.push({
        label: t('pricing.featureLabels.categoriesLimit').replace('{count}', String(plan.categoryLimitMonthly)),
        included: true,
      });
    }
    
    // SEO básico
    features.push({
      label: t('pricing.featureLabels.seoTitle'),
      included: true,
    });
    
    features.push({
      label: t('pricing.featureLabels.shortDesc'),
      included: true,
    });
    
    // Long description
    features.push({
      label: t('pricing.featureLabels.longDesc'),
      included: plan.features.long_description,
      highlight: plan.features.long_description,
    });
    
    // Bulk
    features.push({
      label: t('pricing.featureLabels.bulk'),
      included: plan.features.bulk_processing,
      highlight: plan.features.bulk_processing,
    });
    
    // Regeneration
    if (plan.features.regeneration) {
      features.push({
        label: t('pricing.featureLabels.regeneration'),
        included: true,
      });
    }
    
    // History
    if (plan.features.history) {
      features.push({
        label: t('pricing.featureLabels.history'),
        included: true,
      });
    }
    
    // Language detection - included in all plans
    features.push({
      label: t('pricing.featureLabels.language'),
      included: true,
    });
    
    // Auto processing (Scale only)
    if (plan.features.auto_processing) {
      features.push({
        label: t('pricing.featureLabels.autoProcess'),
        included: true,
        highlight: true,
      });
    }
    
    // Smart review (Scale only)
    if (plan.features.smart_review) {
      features.push({
        label: t('pricing.featureLabels.smartReview'),
        included: true,
        highlight: true,
      });
    }

    // Keyword Research (Pro+) with descriptive limits
    const kwLabelKey = !plan.features.keyword_research
      ? 'pricing.featureLabels.keywordResearch'
      : plan.planType === 'pro'
        ? 'pricing.featureLabels.keywordResearchPro'
        : plan.planType === 'scale'
          ? 'pricing.featureLabels.keywordResearchScale'
          : 'pricing.featureLabels.keywordResearchAgency';

    features.push({
      label: t(kwLabelKey),
      included: plan.features.keyword_research,
      highlight: plan.features.keyword_research,
    });

    return features;
  };

  const agencyPlan = plans.find(p => p.planType === 'agency');

  // TikTok ViewContent when pricing section is visible
  const sectionRef = useRef<HTMLElement>(null);
  const viewTracked = useRef(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !viewTracked.current) {
        viewTracked.current = true;
        trackTikTokEvent("ViewContent", { content_id: "pricing_section", content_type: "product" });
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="pricing" className="py-20 md:py-32 relative overflow-hidden" ref={sectionRef}>
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="glow-orb glow-orb-primary w-[600px] h-[600px] top-0 left-1/4 opacity-15" />
        <div className="glow-orb glow-orb-accent w-[500px] h-[500px] bottom-0 right-1/4 opacity-15" />
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-sm font-medium mb-6">
            <Zap size={16} className="text-primary" />
            <span className="text-foreground">{t('pricing.badge')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t('pricing.title')}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t('pricing.subtitle')}
          </p>

          {/* Monthly/Annual Toggle */}
          <div className="mt-8 inline-flex items-center gap-3">
            <span className={cn("text-sm font-medium transition-colors", !isAnnual ? "text-foreground" : "text-muted-foreground")}>
              {t('pricing.monthlyToggle')}
            </span>
            <Switch checked={isAnnual} onCheckedChange={setIsAnnual} />
            <span className={cn("text-sm font-medium transition-colors", isAnnual ? "text-foreground" : "text-muted-foreground")}>
              {t('pricing.annualToggle')}
            </span>
            <span className="inline-flex px-2 py-0.5 rounded-full bg-accent text-accent-foreground text-xs font-semibold">
              -26%
            </span>
          </div>
        </div>

        {/* Main 4 plans grid */}
        <div className="max-w-6xl mx-auto pt-6">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1,2,3,4].map(i => <Skeleton key={i} className="h-96 rounded-2xl" />)}
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.filter(p => p.planType !== 'agency').map((plan) => {
            const features = getPlanFeaturesTranslated(plan);
            
            return (
              <div key={plan.id} className="relative pt-4">
                {plan.popular && (
                  <div className="absolute top-1 left-1/2 -translate-x-1/2 z-10">
                    <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-gradient-to-r from-primary to-accent text-white text-xs font-semibold shadow-glow">
                      <Sparkles size={12} />
                      {t('pricing.popular')}
                    </span>
                  </div>
                )}
                {plan.badge && !plan.popular && (
                   <div className="absolute top-1 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                    ⭐ {t(`pricing.plans.${plan.planType}.badge`)}
                  </div>
                )}
                <div
                    className={cn(
                    "relative rounded-2xl p-4 md:p-6 flex flex-col glass-card hover-lift transition-all duration-300 h-full",
                    plan.popular
                      ? "gradient-border-animated"
                      : "border border-border/30"
                  )}
                >

                <div className="text-center mb-6 pt-2">
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t(`pricing.plans.${plan.planType}.description`)}
                  </p>
                  
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-3xl md:text-4xl font-extrabold text-foreground">
                        ${isAnnual && plan.priceAnnual > 0 ? Math.round(plan.priceAnnual / 12) : plan.priceMonthly}
                      </span>
                      {(plan.priceMonthly > 0 || plan.priceAnnual > 0) && (
                        <span className="text-muted-foreground text-sm">
                          {t('pricing.perMonth')}
                        </span>
                      )}
                    </div>
                    {isAnnual && plan.priceAnnual > 0 && (
                      <span className="text-xs text-muted-foreground">
                        ${plan.priceAnnual}{t('pricing.annual')} · {t('pricing.billedAnnually')}
                      </span>
                    )}
                    {isAnnual && plan.priceAnnual > 0 && (
                      <span className="text-xs font-semibold text-accent">
                        {t('pricing.annualSave').replace('{percent}', String(Math.round((1 - plan.priceAnnual / (plan.priceMonthly * 12)) * 100)))}
                      </span>
                    )}
                    {plan.creditsMonthly > 0 && plan.priceMonthly > 0 && (
                      <div className="mt-2 text-center">
                        <span className="text-xs text-muted-foreground">
                          {t('pricing.perProduct').replace('{cost}', (plan.priceMonthly / plan.creditsMonthly).toFixed(2))}
                        </span>
                        <br />
                        <span className="text-xs text-muted-foreground/60 line-through">
                          {t('pricing.copywriterCost')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <ul className="space-y-3 mb-6 flex-1">
                  {features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      {feature.included ? (
                        <Check className={cn(
                          "w-5 h-5 mt-0.5 flex-shrink-0",
                          feature.highlight ? "text-accent" : "text-primary"
                        )} />
                      ) : (
                        <X className="w-5 h-5 mt-0.5 flex-shrink-0 text-muted-foreground/40" />
                      )}
                      <span className={cn(
                        "text-sm",
                        !feature.included && "line-through opacity-50 text-muted-foreground",
                        feature.included && "text-muted-foreground"
                      )}>
                        {feature.label}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link to={plan.priceMonthly > 0 ? `/auth?plan=${plan.planType}&period=${isAnnual ? 'annual' : 'monthly'}` : '/auth'} className="mt-auto">
                  <Button
                    variant={plan.priceMonthly > 0 ? "cta" : "outline"}
                    className="w-full"
                    size="lg"
                  >
                    {t(`pricing.plans.${plan.planType}.cta`)}
                  </Button>
                </Link>
                </div>
              </div>
            );
          })}
        </div>
          )}
        </div>

        {/* Agency Plan - Featured Section */}
        {agencyPlan && (
          <div className="mt-12 max-w-4xl mx-auto">
            <div className="relative p-5 md:p-8 rounded-2xl glass-card gradient-border-animated">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                {/* Left - Info */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">{agencyPlan.name}</h3>
                      <p className="text-muted-foreground text-sm">{t('pricing.agencySubtitle')}</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground mb-6">
                    {t('pricing.plans.agency.description')}
                  </p>
                  <div className="flex flex-col gap-1 mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-extrabold text-foreground">
                        ${isAnnual ? Math.round(agencyPlan.priceAnnual / 12) : agencyPlan.priceMonthly}
                      </span>
                      <span className="text-muted-foreground">{t('pricing.perMonth')}</span>
                    </div>
                    {isAnnual && (
                      <span className="text-sm text-muted-foreground">
                        ${agencyPlan.priceAnnual}{t('pricing.annual')} · {t('pricing.billedAnnually')}
                      </span>
                    )}
                  </div>
                  <Link to={`/auth?plan=agency&period=${isAnnual ? 'annual' : 'monthly'}`}>
                    <Button variant="cta" size="xl" className="group">
                      {t('pricing.plans.agency.cta')}
                      <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                </div>

                {/* Right - Features */}
                <div className="space-y-3">
                  {[
                    t('pricing.agencyFeatures.credits').replace('{count}', agencyPlan.creditsMonthly.toLocaleString()),
                    t('pricing.agencyFeatures.stores').replace('{count}', String(agencyPlan.storeLimit)),
                    t('pricing.agencyFeatures.categories'),
                    t('pricing.agencyFeatures.api'),
                    t('pricing.agencyFeatures.support'),
                    t('pricing.agencyFeatures.autoProcess'),
                    t('pricing.agencyFeatures.smartReview'),
                    t('pricing.agencyFeatures.priorityGen'),
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-primary" />
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Guarantee + Trust bar */}
        <div className="mt-12 space-y-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl glass-card gradient-border-animated">
              <Gift className="w-8 h-8 text-accent flex-shrink-0" />
              <div className="text-left">
                <p className="font-semibold text-foreground text-sm">{t('pricing.guarantee.title')}</p>
                <p className="text-xs text-muted-foreground">{t('pricing.guarantee.subtitle')}</p>
              </div>
            </div>
          </div>
          <div className="text-center">
            <div className="inline-flex flex-wrap items-center justify-center gap-x-8 gap-y-3 px-6 py-4 rounded-2xl glass-card">
              {[
                t('pricing.trust.noContracts'),
                t('pricing.trust.cancelOneClick'),
                t('pricing.trust.spanishSupport'),
                t('pricing.trust.securePay'),
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-primary" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
